import React from "react";
import { motion } from "framer-motion";

/**
 * SandrineIntro — Sandrine introduction section, imageless by design.
 *
 * In place of a portrait, we feature one of her actual Centre affirmations
 * as a sample card. It signs the section with her hand more honestly than
 * a stock photo could, and stays inside the page's hand-drawn aesthetic.
 */
export default function SandrineIntro() {
  return (
    <section
      id="sandrine"
      className="relative py-24 md:py-32"
      data-testid="sandrine-intro"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
        {/* Sample affirmation card — pinned/tilted "note" in Centre yellow */}
        <motion.div
          initial={{ opacity: 0, x: -16, rotate: -3.4 }}
          whileInView={{ opacity: 1, x: 0, rotate: -2.4 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="md:col-span-5 relative mx-auto md:mx-0 max-w-md"
          data-testid="sandrine-sample-card"
        >
          {/* Soft halo behind the card */}
          <span
            aria-hidden
            className="absolute -inset-6 rounded-[28px] opacity-60"
            style={{
              backgroundColor: "#fae2b5",
              filter: "blur(28px)",
            }}
          />

          <div
            className="relative rounded-2xl border-2 px-7 md:px-9 py-9 md:py-11 shadow-[0_18px_50px_rgba(60,40,10,0.12)]"
            style={{
              backgroundColor: "#fae2b5",
              borderColor: "#3d2a14",
            }}
          >
            {/* Top-corner pin — hand-drawn */}
            <svg
              aria-hidden
              className="absolute -top-3 left-6 md:left-8"
              width="36"
              height="36"
              viewBox="0 0 36 36"
            >
              <circle cx="18" cy="18" r="10" fill="#e93e36" stroke="#3d2a14" strokeWidth="2" />
              <ellipse cx="14" cy="14" rx="3" ry="2" fill="#ffffff" opacity="0.5" />
              <path
                d="M18 25 L18 34"
                stroke="#3d2a14"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>

            <p
              className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-5"
              style={{ color: "#7a5816" }}
              data-testid="sample-card-eyebrow"
            >
              A sample of the Centre yarn
            </p>

            <p
              className="text-xl md:text-[1.55rem] leading-snug font-medium text-[#1a1a1a]"
              style={{ fontFamily: "Rubik, sans-serif" }}
              data-testid="sample-card-affirmation"
            >
              You are allowed to build slowly. The thing you are making does
              not yet exist; it cannot be late.
            </p>

            {/* Signature */}
            <div className="mt-7 flex items-end justify-between gap-4">
              <div>
                <p
                  className="text-2xl md:text-3xl text-[#1a1a1a]"
                  style={{
                    fontFamily: "Rubik, sans-serif",
                    fontStyle: "italic",
                    fontWeight: 500,
                    letterSpacing: "-0.01em",
                  }}
                  data-testid="sandrine-signature"
                >
                  — Sandrine
                </p>
                {/* Hand-drawn signature underline */}
                <svg
                  aria-hidden
                  width="170"
                  height="14"
                  viewBox="0 0 170 14"
                  className="mt-0.5"
                >
                  <path
                    d="M 4 8 Q 30 2, 60 7 T 120 6 T 166 4"
                    stroke="#3d2a14"
                    strokeWidth="1.6"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
                <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-[#7a5816]">
                  Head of Community Wellbeing
                </p>
              </div>
              {/* small Centre-yarn ball mark */}
              <svg
                aria-hidden
                width="44"
                height="44"
                viewBox="0 0 44 44"
                className="shrink-0"
              >
                <circle cx="22" cy="22" r="16" fill="#f8d799" stroke="#3d2a14" strokeWidth="2" />
                {[0, 1, 2, 3].map((i) => (
                  <path
                    key={i}
                    d={`M 8 ${16 + i * 4} Q 22 ${12 + i * 4}, 36 ${16 + i * 4}`}
                    stroke="#b8862e"
                    strokeWidth="1"
                    fill="none"
                    opacity="0.6"
                  />
                ))}
                <path
                  d="M 36 22 Q 40 26, 42 32"
                  stroke="#b8862e"
                  strokeWidth="1.2"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="md:col-span-7"
        >
          <p className="text-xs uppercase tracking-[0.22em] text-weave-teal font-medium mb-4">
            The signature of the Centre yarn
          </p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-[#1a1a1a] leading-[1.1]">
            Written by hand.
            <span className="block text-[#3a3a3a]/70 font-medium">
              Not by a model.
            </span>
          </h2>
          <p className="mt-6 text-lg text-[#3a3a3a]/90 leading-relaxed">
            Every Centre affirmation is written by{" "}
            <span className="font-medium text-[#1a1a1a]">Sandrine</span>, The
            Weave's Head of Community Wellbeing. No template, no scrape, no
            generation. Founder-appropriate, never saccharine — the kind of
            line you'd read aloud to a friend who needed it on a Tuesday.
          </p>
          <p className="mt-4 text-base text-[#3a3a3a]/80 leading-relaxed">
            The library refreshes quarterly. Her voice is the part of the loom
            we will not let drift.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2"
              style={{
                backgroundColor: "#fae2b5",
                borderColor: "#3d2a14",
                color: "#7a5816",
              }}
              data-testid="sandrine-yarn-badge"
            >
              <span
                className="block w-2 h-2 rounded-full"
                style={{ backgroundColor: "#b8862e" }}
                aria-hidden
              />
              <span className="text-sm font-medium">Centre · refreshed quarterly</span>
            </span>
            <span className="text-sm text-[#3a3a3a]/65">
              · Written for The 1% Club
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
